import React from 'react';
import { Box, Paper, Stack, Typography } from '../../ui/core';

/**
 * The three mutually-exclusive marker states from `getStationVerdictForPair`
 * (see laneEligibility.js). A station with no lane context at all (rendered
 * when no pair is selected) doesn't get a row here — it's a fourth, neutral
 * style that means "not evaluated", not a fourth verdict.
 *
 * Labels are deliberately terse: this card floats over the map, and the first
 * version — full-sentence labels plus a paragraph of caveat — covered about a
 * third of the viewport, including the far end of a long lane like
 * Austin↔Houston. The longer wording lives in `hint` and surfaces on hover.
 */
const SWATCHES = [
  {
    color: '#1B5E20',
    filled: true,
    label: 'Eligible',
    hint: 'Usable for pickup or dropoff on at least one route in this pair',
  },
  {
    color: '#EF6C00',
    filled: false,
    label: 'In corridor, unusable',
    hint: 'Inside some route’s corridor but qualifies for neither pickup nor dropoff',
  },
  {
    color: '#9E9E9E',
    filled: true,
    small: true,
    label: 'Outside corridor',
    hint: 'Beyond every route corridor in this pair',
  },
];

/**
 * Shown only while non-operational stations are being displayed — the toggle
 * is off by default, and a legend row for markers that aren't on screen is
 * just clutter. Non-operational beats every verdict in `stationIcon`, which is
 * why it reads as its own state here rather than a variant of one.
 */
const NON_OPERATIONAL_SWATCH = {
  color: '#9E9E9E',
  faded: true,
  label: 'Not operational',
  hint: 'Station is switched off — never usable, whatever the limits are',
};

/**
 * Rendered as a floating card inside the map container (see LaneMap.js) so
 * it's visible without scrolling the left panel — it used to sit below the
 * limits panel, where the page owner never saw it.
 *
 * The usable-station count is deliberately NOT repeated here; the selected
 * pair's sidebar row already carries it.
 */
const LaneMapLegend = ({ showNonOperational = false }) => (
  <Paper elevation={3} sx={{ px: 1.25, py: 1, maxWidth: 220, opacity: 0.94 }}>
    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
      Stations
    </Typography>
    <Stack spacing={0.25}>
      {[...SWATCHES, ...(showNonOperational ? [NON_OPERATIONAL_SWATCH] : [])].map(
        ({ color, filled, small, faded, label, hint }) => (
          <Stack key={label} direction="row" spacing={0.75} alignItems="center" title={hint}>
            <Box
              sx={{
                width: small ? 7 : 10,
                height: small ? 7 : 10,
                borderRadius: '50%',
                backgroundColor: filled || faded ? color : 'transparent',
                border: `2px solid ${color}`,
                opacity: faded ? 0.45 : 1,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" noWrap sx={{ opacity: faded ? 0.7 : 1 }}>
              {label}
            </Typography>
          </Stack>
        ),
      )}
    </Stack>

    {/*
      Kept short but kept: without it the map reads as "this is the station the
      rider gets", which it is not — production picks per-rider from their own
      coordinates, which this page has no access to.
    */}
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', mt: 0.75, lineHeight: 1.3 }}
      title="Production picks per-rider from their own coordinates, which this map does not have."
    >
      What each station is usable for — not what a rider is offered.
    </Typography>
  </Paper>
);

export default LaneMapLegend;
